import React, { useState, useRef, useEffect } from 'react';
import RotatorForm from '../components/RotatorForm';
import ProjectsOrbit from '../components/ProjectsOrbit';
import './Startseite.css';

function Startseite() {
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  // Prevent right-click context menu on images and videos
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.tagName === 'IMG' || e.target.tagName === 'VIDEO') {
        e.preventDefault();
        return false;
      }
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  // Deep links (#cta-section / #wie-lege-ich-los), z.B. von den Projektseiten oder alten "Wie lege ich los"-Links
  useEffect(() => {
    const id = (window.location.hash || '').replace('#', '');
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 400);
  }, []);

  return (
    <div className="startseite" data-testid="startseite-page">

      {/* Video Section */}
      <section className="video-section">
        <div className="video-container">
          <video
            ref={videoRef}
            className="intro-video"
            autoPlay
            muted
            playsInline
            controls
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            data-testid="intro-video"
          >
            <source src={`${API_URL}/api/files/freiheitsmacher/video/trailer_1080p_optimized.mp4`} type="video/mp4" />
            Ihr Browser unterstützt das Video-Tag nicht.
          </video>
          <button className="mute-button" onClick={toggleMute} aria-label={isMuted ? 'Ton einschalten' : 'Ton ausschalten'} data-testid="mute-button">
            {isMuted ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <line x1="23" y1="9" x2="17" y2="15"></line>
                <line x1="17" y1="9" x2="23" y2="15"></line>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
              </svg>
            )}
          </button>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title" data-testid="hero-title">Die Freiheitsmacher</h1>
            <p className="hero-subtitle">Deine Gemeinschaft für finanzielle Freiheit und Selbstbestimmung</p>
          </div>
        </div>
      </section>

      {/* Gründer + Community (ein gemeinsamer Abschnitt) */}
      <section className="founder-section section" id="gruender">
        <div className="container">
          <h2 className="section-title" data-testid="founder-section-title">Die Gründer der Freiheitsmacher: Anke und Volker</h2>

          <div className="founders-images-grid">
            <div className="founder-image-container" data-testid="founder-image-anke">
              <img src="https://customer-assets.emergentagent.com/job_e5c77d79-ca13-402d-b554-25dcc110651b/artifacts/6odp9gli_image.png" alt="Anke Dittrich" className="founder-image" draggable="false" onContextMenu={(e) => e.preventDefault()} />
              <p className="founder-name">Anke</p>
            </div>
            <div className="founder-image-container" data-testid="founder-image-volker">
              <img src="https://customer-assets.emergentagent.com/job_e5c77d79-ca13-402d-b554-25dcc110651b/artifacts/o8754ufl_image.png" alt="Volker Baumgart" className="founder-image" draggable="false" onContextMenu={(e) => e.preventDefault()} />
              <p className="founder-name">Volker</p>
            </div>
          </div>

          <div className="founder-story" data-testid="founder-story">
            <h3>Anke und Volker – Die Gründer der Freiheitsmacher</h3>
            <p data-testid="community-intro">
              Seit April 2025 haben wir unsere Gemeinschaft „Die Freiheitsmacher" gegründet. In dieser Gruppe verbinden wir Menschen, die Freiheit und Selbstbestimmung in ihrem Leben suchen. Wir unterstützen uns gegenseitig dabei, die Kontrolle über das eigene Geld zu behalten, es geschickt für sich arbeiten zu lassen und – wenn gewünscht – ein nebenberufliches zweites Standbein aufzubauen. Dabei stehen Zusammenhalt, gegenseitige Unterstützung und der Austausch von Wissen im Mittelpunkt. Wenn du Lust hast, deine Unabhängigkeit zu stärken und Teil einer starken Gemeinschaft zu sein, bist du bei uns genau richtig!
            </p>
            <p>
              Wie kam es eigentlich zur Gründung dieser Gruppe? Ganz einfach: Auf der Isla Margarita hatten wir ursprünglich eine Gruppe namens Isla4You ins Leben gerufen. Unser Ziel war es, Menschen zu unterstützen, die den Schritt auf die Isla Margarita wagen und auswandern möchten. Durch unsere Erfahrungen und Kontakte konnten wir bereits einige Fehler vermeiden – und genau das wollten wir weitergeben, damit andere den Weg abkürzen und von Anfang an alles richtig machen.
            </p>
            <p>
              Im Laufe vieler und langer Telefongespräche wurde klar, dass Auswandern zwar ein großes Thema ist, doch noch wichtiger ist das Thema Geld verdienen – insbesondere online und passiv. Das Thema spricht nicht nur Auswanderer an, sondern auch jene, die flexibel und ortsunabhängig arbeiten möchten. So entstand die Gruppe „Die Freiheitsmacher".
            </p>
            <p>
              Wir sind in allen Projekten selbst investiert und treten transparent auf. In unseren Calls zeigen wir euch sogar unsere Konten, denn wir wollen euch aktiv in die neue digitale Krypto-Welt führen. Dies ist ein entscheidender Schritt für die Zukunft – und wir gehen ihn gemeinsam.
            </p>
            <p>
              Bei uns endet es nicht mit dem Einstieg in ein Projekt – dann fängt es erst richtig an. Es gibt verschiedene Projekte, die für jeden etwas bieten. Einen Überblick dazu findest du weiter unten unter „Wie lege ich los?".
            </p>
            <p>
              Ich bin auch sehr stolz auf unser großartiges Leader-Team, das sich seit Ende April 2025 entwickelt hat. Ihr seid hier wirklich gut aufgehoben und werdet mit Anleitung und Begleitung die nächsten Schritte in eine freie Welt machen können.
            </p>
            <p>
              Außerdem sind wir zu einer wunderbaren Herz-Community zusammengewachsen, in der neben dem gemeinsamen Ernst auch viel gelacht wird. Gerade in der heutigen Zeit ist das besonders wichtig und bereichernd für uns alle. Auch mit unserem Leader-Team sind tolle Freundschaften entstanden – so wertvoll.
            </p>
            <p>
              Ich freue mich sehr, dass du hier auf unserer Seite bist und wünsche dir viel Spaß beim Stöbern.
            </p>
          </div>
        </div>
      </section>

      {/* Wie lege ich los? (ehemals eigene Seite) */}
      <section className="start-section section" id="wie-lege-ich-los" data-testid="wie-lege-ich-los-section">
        <div className="container">
          <div className="start-head">
            <h2>Wie lege ich los?</h2>
            <p className="page-subtitle">Dein Weg zur Freiheit und ortsunabhängigem Arbeiten</p>
          </div>

          <div className="content-intro">
            <h3>Dein Start in ein freies und selbstbestimmtes Leben</h3>
            <p className="intro-text">
              Stell dir vor, du könntest arbeiten, wo immer du möchtest – am Strand, im Lieblingscafé oder gemütlich von zu Hause aus. Mit den richtigen Tipps und Strategien wird dieser Traum Wirklichkeit, denn ortsunabhängiges Arbeiten macht dich flexibel und frei.
            </p>
          </div>

          <div className="content-block">
            <p>
              Für viele bedeutet das nicht nur mehr Freiheit, sondern die Möglichkeit, den Alltag aktiv zu gestalten und mehr Zeit für die wichtigen Dinge zu haben – wie gemeinsame Momente mit der Familie.
            </p>
            <p>
              Ein finanzielles zweites Standbein nebenbei aufzubauen, bietet Sicherheit und wächst mit dir. Viele in unserer Gemeinschaft haben den Weg zur vollständigen Unabhängigkeit schon eingeschlagen. Passives Einkommen spielt dabei eine zentrale Rolle – egal ob durch kluge Investitionen oder den Aufbau eines Teams.
            </p>
            <p className="highlight-text">
              Das Beste daran: Du bist nicht alleine. Unsere Gemeinschaft stärkt dich, begleitet dich persönlich und sorgt dafür, dass du Schritt für Schritt dein Ziel erreichst.
            </p>
          </div>

          <div className="projects-section" id="projekte" data-testid="projects-section">
            <h2>Unsere Projekte</h2>
            <p className="projects-intro">
              Hier findest du eine Übersicht unserer verschiedenen Projekte. Jedes davon bietet dir einzigartige Möglichkeiten, um deine Ziele von Freiheit, ortsunabhängigem Arbeiten und finanzieller Unabhängigkeit zu erreichen. Fahre über ein Projekt oder tippe es an, um mehr zu erfahren – wenn du Fragen hast, stehen wir dir jederzeit zur Verfügung.
            </p>
            <ProjectsOrbit />
          </div>

          <div className="cta-section" id="cta-section">
            <h2>Bereit, den ersten Schritt zu machen?</h2>
            <p>Werde Teil unserer Gemeinschaft und starte deine Reise zur finanziellen Freiheit. Trag dich ein – dein persönlicher Förderer meldet sich bei dir.</p>
            <RotatorForm />
          </div>
        </div>
      </section>
    </div>
  );
}

export default Startseite;
